import React from 'react'
import {
  MetricCard,
  StatsCard,
  Divider,
  DemographicsSection,
  DescriptionAndContactInfo,
} from 'components/NewCompanyPage/Sidebar/shared'
import { formatInteger, formatAddress } from 'utils/formatting'
import { toLegacyAddressInfo } from 'services/k12'
import { GetK12DistrictQueryResponse } from 'services/generated/types/GetK12District'
import { GoogleMapsLink } from '../utils/GoogleMapsLink'
import { BuildCompanyTypeContentOptions, CompanyTypePageContent } from './types'

/**
 * Shared K12-district content derivation used by both `NewK12DistrictPage`
 * (desktop) and `MobileK12Page`. See {@link CompanyTypePageContent}.
 */
export function buildK12PageContent(
  data: GetK12DistrictQueryResponse | undefined,
  { onViewDoors }: BuildCompanyTypeContentOptions = {}
): CompanyTypePageContent {
  const k12district = data?.k12district

  const operationalStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Total Students',
      value: k12district?.student_count
        ? formatInteger(k12district.student_count)
        : 'N/A',
    },
    {
      label: 'Total Teachers',
      value: k12district?.teachers_count
        ? formatInteger(k12district.teachers_count)
        : 'N/A',
    },
    {
      label: 'Operational Schools',
      value: k12district?.operational_schools
        ? formatInteger(k12district.operational_schools)
        : 'N/A',
    },
    { label: 'County', value: k12district?.county || 'N/A' },
    { label: 'Locale Type', value: k12district?.locale || 'N/A' },
    { label: 'Status', value: k12district?.sy_status_text || 'N/A' },
  ]

  const financialStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Total Revenue',
      value: k12district?.revenue
        ? `$${formatInteger(k12district.revenue)}`
        : 'N/A',
    },
    {
      label: 'Food Service Expenditures',
      value: k12district?.food_service_expenditures
        ? `$${formatInteger(k12district.food_service_expenditures)}`
        : 'N/A',
    },
    {
      label: 'Estimated Meals/Day',
      value: k12district?.estimated_meals_per_day
        ? formatInteger(k12district.estimated_meals_per_day)
        : 'N/A',
    },
  ]

  const lunchProgramStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Free Lunch Students',
      value: k12district?.free_lunch
        ? `${formatInteger(k12district.free_lunch)} (${k12district.free_lunch_perc?.toFixed(1) || '0'}%)`
        : 'N/A',
    },
    {
      label: 'Reduced Lunch Students',
      value: k12district?.reduced_lunch
        ? `${formatInteger(k12district.reduced_lunch)} (${k12district.reduced_lunch_perc?.toFixed(1) || '0'}%)`
        : 'N/A',
    },
    {
      label: 'Total Supplemented Lunch',
      value: k12district?.total_supplemented_lunch
        ? `${formatInteger(k12district.total_supplemented_lunch)} (${k12district.total_supplemented_lunch_perc?.toFixed(1) || '0'}%)`
        : 'N/A',
    },
  ]

  const summaryContent: React.ReactNode[] = []

  summaryContent.push(
    <DescriptionAndContactInfo
      key="description-contact"
      phone={k12district?.phone ?? undefined}
      googlePlaceUrl={k12district?.google_place_url ?? undefined}
      address={
        formatAddress({
          street: k12district?.address,
          city: k12district?.lcity,
          state: k12district?.lstate,
          zip: k12district?.zipcode,
        }) ?? undefined
      }
    />
  )

  if (onViewDoors && data?.doors_count && data.doors_count > 0) {
    summaryContent.push(
      <MetricCard
        key="doors"
        value={data.doors_count}
        label="Door Count"
        actionText="View Doors"
        onActionClick={onViewDoors}
      />
    )
  }

  summaryContent.push(
    <StatsCard key="operational-stats" stats={operationalStats} />,
    <Divider key="divider-1" />,
    <StatsCard key="financial-stats" stats={financialStats} />,
    <Divider key="divider-2" />,
    <StatsCard key="lunch-program-stats" stats={lunchProgramStats} />
  )

  const districtLocation = k12district?.address
    ? `${k12district.lcity}, ${k12district.lstate} - USA`
    : 'N/A'

  const locationInfoStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Full Address',
      value: <GoogleMapsLink address={k12district?.address} />,
    },
    { label: 'ZIP Code', value: k12district?.zipcode || 'N/A' },
    { label: 'State', value: k12district?.lstate || 'N/A' },
    { label: 'City', value: k12district?.lcity || 'N/A' },
    { label: 'Phone', value: k12district?.phone || 'N/A' },
    { label: 'School Year', value: k12district?.school_year || 'N/A' },
  ]

  const locationDetailsContent: React.ReactNode[] = [
    <MetricCard
      key="district-location"
      value={districtLocation}
      label="District Location"
      variant="full-width"
    />,
    <StatsCard key="location-info" stats={locationInfoStats} />,
  ]

  const legacyAddressInfo = toLegacyAddressInfo(k12district?.address_info)
  if (legacyAddressInfo) {
    locationDetailsContent.push(
      <Divider key="divider-3" />,
      <DemographicsSection
        key="zipcode-demographics"
        addressInfo={legacyAddressInfo}
      />
    )
  }

  return {
    documentTitle: `${k12district?.lea_name || 'Not Found'} (K12 District) - First Bite`,
    header: {
      name: k12district?.lea_name ?? '',
      domain: k12district?.website ?? undefined,
      logoUrl: undefined,
    },
    contactCompanyId: data?.id,
    doorsCount: data?.doors_count ?? 0,
    summaryContent,
    locationDetailsContent,
    breadcrumbRoot: 'Education - K12 District',
    breadcrumbTitle: 'K12 District',
    categoryInfo: {
      categorySlug: 'non-commercial',
      subCategorySlug: 'education-k-12',
    },
    companyTypeSlug: 'education-k-12',
    tableTypeSlug: 'education-k-12',
    modalSubCategory: {
      name: 'K-12 Education',
      slug: 'education-k-12',
      description: 'K-12 Education',
    },
  }
}
